// IM-UX-002 (IM-UI-080) — análise recomendada, compartilhada por PESTEL/Cinco Forças e TAM/SAM/SOM:
//  - RecommendedContext: resumo curto do que será usado (valor + origem), conflitos a decidir (sem pré-seleção) e, sob
//    demanda, a origem detalhada, o que falta, a confiança esperada e as limitações.
//  - useSubmitOnce: trava síncrona contra duplo clique/reenvio (o estado do React chega tarde para o 2º clique).
//  - GenerationStatus: estado claro da geração (gerando, falha com nova tentativa só manual).
import { useCallback, useRef, useState, type ReactNode } from 'react';
import {
  RECOMMENDATION_ORIGIN_LABEL, REQUIREMENT_STATUS_LABEL,
  type CreationProvenance, type ExpectedConfidence, type RecommendationConflict, type RecommendationOrigin, type RecommendationRequirement, type RecommendedValue,
} from '@intelimove/contracts';
import { Disclosure } from './Guidance';
import { StatusPill } from './Widgets';

const ORIGIN_TONE: Record<RecommendationOrigin, string> = { CONFIRMADO: 'mint', ATIVO: 'mint', ANALISE_RECENTE: 'mint', INFERIDO: 'amber', PADRAO: 'gray', ESCOLHA: 'amber' };
const REQ_TONE: Record<string, string> = { CONFIRMADA: 'mint', INFERIDA: 'amber', AUSENTE_NAO_BLOQUEANTE: 'gray', AUSENTE_INDISPENSAVEL: 'critical' };
export const CONFIDENCE_TXT: Record<ExpectedConfidence, [string, string]> = { ALTA: ['alta', 'mint'], MEDIA: ['média', 'amber'], BAIXA: ['baixa', 'critical'] };

export function OriginPill({ origin }: { origin: RecommendationOrigin }) {
  return <StatusPill tone={ORIGIN_TONE[origin]}>{RECOMMENDATION_ORIGIN_LABEL[origin]}</StatusPill>;
}

/** Valor exibido: o atual (pode ter sido alterado em "Personalizar análise") com a origem; alterado = "escolhido por você". */
export interface ShownValue extends RecommendedValue { current?: string; changed?: boolean }

export function RecommendedContext({ values, conflicts, choices, onChoose, requirements, confidence, confidenceReason, limitations, extra }: {
  values: ShownValue[];
  conflicts: RecommendationConflict[];
  choices: Record<string, string | undefined>;
  onChoose: (field: string, key: string) => void;
  requirements: RecommendationRequirement[];
  confidence: ExpectedConfidence; confidenceReason: string;
  limitations: string[];
  /** Bloco extra (ex.: pedido mínimo do que falta), entre o resumo e a origem detalhada. */
  extra?: ReactNode;
}) {
  const missing = requirements.filter((r) => r.status !== 'CONFIRMADA');
  const [ct, tone] = CONFIDENCE_TXT[confidence];
  return (
    <div className="rc-box">
      <h3 className="rc-title">O que será usado</h3>
      <dl className="rc-values">
        {values.filter((v) => v.field !== 'name').map((v) => (
          <div key={v.field}>
            <dt>{v.label}</dt>
            <dd><span className="rc-v">{v.changed ? v.current : v.value}</span> <OriginPill origin={v.changed ? 'ESCOLHA' : v.origin} />
              {v.review && !v.changed && <span className="cell-sub inline"> · revise se quiser</span>}</dd>
          </div>
        ))}
      </dl>
      {conflicts.map((c) => (
        <fieldset key={c.field} className="rc-conflict">
          <legend>{c.question}</legend>
          <p className="cell-sub wide">{c.reason}</p>
          {c.options.map((o) => (
            <label key={o.key} className="sz-check"><input type="radio" name={`rc-${c.field}`} checked={choices[c.field] === o.key} onChange={() => onChoose(c.field, o.key)} /> {o.label}
              <span className="cell-sub inline"> · {RECOMMENDATION_ORIGIN_LABEL[o.origin]} — {o.source}</span></label>
          ))}
        </fieldset>
      ))}
      <p className="rc-line">Confiança esperada: <StatusPill tone={tone}>{ct}</StatusPill> <span className="cell-sub inline">{confidenceReason}</span></p>
      {missing.length > 0 && <p className="rc-line rc-missing">Ainda falta ou é inferido: {missing.map((r) => `${r.label} (${REQUIREMENT_STATUS_LABEL[r.status]})`).join(' · ')}.</p>}
      {extra}
      <Disclosure className="rc-more" title="Origem das informações e limitações" hint="de onde veio cada valor, o que falta e por quê">
        <ul className="sz-list">
          {values.map((v) => <li key={v.field}><b>{v.label}:</b> {v.changed ? <>{v.current} — escolhido por você (recomendado: {v.value})</> : <>{v.value} — {RECOMMENDATION_ORIGIN_LABEL[v.origin]}: {v.source}</>}</li>)}
        </ul>
        <h4 className="gd-h3">Informações necessárias</h4>
        <ul className="sz-list">
          {requirements.map((r) => <li key={r.key}><StatusPill tone={REQ_TONE[r.status] ?? 'gray'}>{REQUIREMENT_STATUS_LABEL[r.status]}</StatusPill> <b>{r.label}</b> — {r.detail}</li>)}
        </ul>
        {limitations.length > 0 && <><h4 className="gd-h3">Limitações</h4><ul className="sz-list">{limitations.map((l) => <li key={l}>{l}</li>)}</ul></>}
        <p className="cell-sub wide">Regra de prioridade: confirmado pela equipe → ativo no cadastro → análise mais recente → inferido com evidência → padrão do InteliMove. Informações conflitantes não são escolhidas por você.</p>
      </Disclosure>
    </div>
  );
}

/** Uma submissão por vez: o 2º clique (antes de o React redesenhar o botão desabilitado) é ignorado. */
export function useSubmitOnce() {
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (fn: () => Promise<void>) => {
    if (lock.current) return;
    lock.current = true; setBusy(true);
    try { await fn(); } finally { lock.current = false; setBusy(false); }
  }, []);
  return { busy, run };
}

/** Estado da geração: gerando (anunciado ao leitor de tela) ou falha (nova tentativa só pelo botão; nada se repete sozinho). */
export function GenerationStatus({ busy, error, busyText }: { busy: boolean; error: string | null; busyText: string }) {
  if (busy) return <p className="rc-status" role="status">{busyText}</p>;
  if (error) return <p className="field-error" role="alert">Falha: {error} Nada foi repetido automaticamente — confira e tente de novo.</p>;
  return null;
}

/** Origem gravada na criação (histórico): modo e de onde veio cada valor. Eventos antigos não têm — nada aparece. */
export function ProvenanceNote({ after }: { after: unknown }) {
  const p = (after as { recommendation?: CreationProvenance } | null)?.recommendation;
  if (!p?.entries?.length) return null;
  return (
    <details className="rc-prov">
      <summary>{p.mode === 'RECOMENDADA' ? 'Criada pela análise recomendada' : 'Criada com personalização'} — origem dos valores</summary>
      <ul className="sz-list">{p.entries.map((e) => <li key={e.field}><b>{e.label}:</b> {e.value} — {RECOMMENDATION_ORIGIN_LABEL[e.origin] ?? e.origin} ({e.source})</li>)}</ul>
    </details>
  );
}
